import React, { useEffect, useCallback } from 'react';
import { useHistory } from 'react-router';
import useScheduleTable from './useScheduleTable';
import { RoomSchedule, RoomV2 } from 'type/room';
import { closeMeetingV2, orderMeeting, setMeetingOrderParams } from 'model/meeting';
import { IMeetingOrderFn, IMeetingOrderForm } from 'type/meeting';
import { useDispatch } from 'react-redux';
import { IMeetingDetail } from 'type/meetingDetail';
import tracker from 'lib/tracker';
import { cloneDeep } from 'lodash-es';
import { includeMasterInMeetingMembers } from 'util/meeting';
import { createMeetingOrderPointHubSuccessResult } from 'lib/pointHub';
import { Modal } from 'antd';

const RoomScheduleTable: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { componentRef, setBlinkList, emitScheduleChange, isDisabledRoom, earlyScheduleDay } = useScheduleTable();

  /** 点击会议室 */
  const onClickRoom = ({ detail }: { detail: [RoomV2] }) => {
    tracker?.reporteMeetingRoom('查看会议室议程');
    const [room] = detail;
    if (room.appId === 'THIRD_PARTY_SYNC_ENTERPRISE_WECHAT') {
      return;
    }
    history.push(`/room-detail/${room.id}`);
  };

  // 检查是否有会议地点
  const hasMeetingLocation = (meeting: any) => {
    const roomBookingForms = meeting?.roomBookingForms || [];
    const roomId = meeting?.roomId;
    const address = meeting?.address;

    // 如果有会议室预约表单、会议室ID或地址，则认为有会议地点
    return (roomBookingForms.length > 0) || !!roomId || !!address;
  };

  // 检查会议时间是否在可预约范围内
  const checkTimeInRange = (meeting: IMeetingOrderForm) => {
    // 只对预约会议进行时间范围检查（repeatMeetingType 为 null 或 0 表示预约会议）
    const meetingAny = meeting as any;
    const repeatMeetingType = meetingAny.repeatMeetingType;
    if (repeatMeetingType !== null && repeatMeetingType !== 0 && repeatMeetingType !== undefined) {
      console.log('非预约会议，跳过时间范围检查');
      return true;
    }

    // earlyScheduleDay 为 0 表示不限制
    if (earlyScheduleDay === 0) {
      console.log('earlyScheduleDay 为 0，不限制时间范围');
      return true;
    }

    // 检查会议开始时间是否在范围内
    const beginTime = meetingAny.beginTime || meetingAny.startTime;
    if (!beginTime) {
      console.log('会议开始时间未设置');
      return true; // 如果没有设置开始时间，允许继续（会有其他验证）
    }

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const maxDate = todayStart + (earlyScheduleDay - 1) * 24 * 3600 * 1000; // 最大可选日期的 00:00
    const beginDate = new Date(beginTime);
    const beginDateOnly = new Date(beginDate.getFullYear(), beginDate.getMonth(), beginDate.getDate()).getTime();
    const isInRange = beginDateOnly <= maxDate;

    console.log('检查会议时间范围：', {
      earlyScheduleDay,
      todayStart: new Date(todayStart).toISOString(),
      maxDate: new Date(maxDate).toISOString(),
      beginTime: new Date(beginTime).toISOString(),
      beginDateOnly: new Date(beginDateOnly).toISOString(),
      isInRange,
    });

    return isInRange;
  };

  /** 点击预约 */
  const onConfirm: IMeetingOrderFn = useCallback((meeting) => {
    console.log('onConfirm MeetingOrderForm ===>', meeting);
    const meetingWithMaster = includeMasterInMeetingMembers(meeting);

    // 如果没有预约会议地点，不进行时间范围检查，允许正常预约
    if (!hasMeetingLocation(meetingWithMaster)) {
      console.log('没有预约会议地点，跳过时间范围检查');
      // 继续后续的预约流程
    } else {
      // 检查预约会议的时间是否在可预约范围内
      if (!checkTimeInRange(meetingWithMaster)) {
        return new Promise((resolve) => {
          resolve({ success: false, errMesaage: '会议时间超出可预约时间范围' });
        });
      }
    }

    let isDisabled = false;
    const roomBookingForms = meetingWithMaster.roomBookingForms || [];
    for (let i = 0; i < roomBookingForms.length; i++) {
      if (isDisabledRoom(roomBookingForms[i].roomId)) {
        isDisabled = true;
        break
      }
    }
    if (isDisabled) {
      return new Promise((resolve) => {
        resolve({ success: false, errMesaage: '第三方会议室暂不支持预约' });
      });
    }
    tracker?.reporteMeetingRoom('预约会议');
    return new Promise((resolve) => {
      dispatch(
        orderMeeting({
          meeting: meetingWithMaster,
          onSuccess: (meeting: IMeetingDetail) => {
            emitScheduleChange();
            setBlinkList(meeting.roomOrderList?.map((room) => room.orderNo) || []);
            resolve(createMeetingOrderPointHubSuccessResult(meeting));
          },
          onFail: (err: any) => {
            if (err?.response?.data?.code === 400403002) {
              const data = err?.response?.data?.data;
              resolve({ success: false, data: data });
            } else if (err?.response?.data?.code === 400406011) {
              resolve({ success: false, errMesaage: '预约人（部门）、会议主题、会议地点可能包含涉黄/涉暴/政治/敏感信息，请检查后重试' });
            } else {
              resolve({ success: false, data: null });
            }
          },
          redirect: false
        })
      );
    });
  }, [earlyScheduleDay, isDisabledRoom, dispatch, emitScheduleChange, setBlinkList]);

  /** 点击预约表单-更多 */
  const onClickOrderMore = (form: IMeetingOrderForm) => {
    tracker?.reporteMeetingRoom('会议室-点击预约表单-更多');
    dispatch(setMeetingOrderParams(cloneDeep(form)));
    history.push('/order-meeting');
  };

  /** 点击日程 */
  const onClickOrderSchedule = ({ detail }: { detail: [RoomSchedule] }) => {
    tracker?.reporteMeetingRoom('查看会议详情');
    const [roomSchedule] = detail;
    history.push({ pathname: '/details/' + roomSchedule.meetingNo });
  };

  /** 点击提前结束 */
  const onClickConcludeSchedule = useCallback(({ detail }: { detail: [RoomSchedule] }) => {
    const [roomSchedule] = detail;
    if (!roomSchedule?.meetingNo) return;

    Modal.confirm({
      title: '提前结束会议',
      icon: '',
      content: '确定要提前结束此会议吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: () => {
        tracker?.reporteMeetingRoom('提前结束');
        dispatch(closeMeetingV2(roomSchedule.meetingNo));
        setTimeout(() => {
          emitScheduleChange();
        }, 1000);
      },
      centered: true,
      zIndex: 10000,
      autoFocusButton: null
    });
  }, [dispatch, emitScheduleChange]);

  const onHeaderConditionChange = (value: any) => {
    const [ov, type] = value?.detail || ['', ''];
    switch (type) {
      case 'dateLast':
      case 'dateNext':
      case 'datePikcer':
        tracker?.reporteMeetingRoom('切换日期');
        break;
      case 'roomNameSearch':
        tracker?.reporteMeetingRoom('搜索会议室');
        break;
      case 'roomCascaderChange':
        tracker?.reporteMeetingRoom('选择会议室');
        break;
      default:
        break;
    }
  };

  /** 属性设置、事件回调监听 */
  useEffect(() => {
    if (!componentRef.current) return;

    componentRef.current.confirm = onConfirm;
    componentRef.current.clickMore = onClickOrderMore;
    componentRef.current.aiRecommendEnabled = false;

    // 初始化-监听事件
    componentRef.current?.addEventListener('click-room', onClickRoom);
    componentRef.current?.addEventListener('click-schedule-detail', onClickOrderSchedule);
    componentRef.current?.addEventListener('click-schedule-conclude', onClickConcludeSchedule);
    componentRef.current?.addEventListener('on-header-condition-change', onHeaderConditionChange);

    // 卸载时-清除监听
    return () => {
      componentRef.current?.removeEventListener('click-room', onClickRoom);
      componentRef.current?.removeEventListener('click-schedule-detail', onClickOrderSchedule);
      componentRef.current?.removeEventListener('click-schedule-conclude', onClickConcludeSchedule);
      componentRef.current?.removeEventListener('on-header-condition-change', onHeaderConditionChange);
    };
  }, [onConfirm, onClickConcludeSchedule]);

  useEffect(() => {
    if (!componentRef.current) return;
    console.log('earlyScheduleDay MeetingOrderForm ===>', earlyScheduleDay);
    componentRef.current.disabledSubsequentDays = earlyScheduleDay;
  }, [earlyScheduleDay]);

  return (
    <>
      <div style={{ width: '100%', height: '100%', margin: 'auto' }}>
        <ce-room-schedule theme="client" ref={componentRef} />
      </div>
    </>
  );
};

export default RoomScheduleTable;
